import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import emailjs from 'emailjs-com';
import { motion, AnimatePresence } from 'framer-motion';
import PrivacyModal from '../Legal/PrivacyModal';

type SendStatus = 'idle' | 'sending' | 'success' | 'error';

interface FormValues {
  nombre: string;
  apellidos: string;
  email: string;
  telefono: string;
  tipoEvento: string;
  tipoServicio: string;
  fecha: string;
  localizacion: string;
  comentarios: string;
  privacidad: boolean;
}

const INITIAL_VALUES: FormValues = {
  nombre: '',
  apellidos: '',
  email: '',
  telefono: '',
  tipoEvento: '',
  tipoServicio: '',
  fecha: '',
  localizacion: '',
  comentarios: '',
  privacidad: false,
};

const SCHEMA = Yup.object({
  nombre: Yup.string().trim().required('Campo obligatorio'),
  apellidos: Yup.string().trim().required('Campo obligatorio'),
  email: Yup.string().email('Introduce un email válido').required('Campo obligatorio'),
  telefono: Yup.string()
    .matches(/^[+\d\s\-()]{9,}$/, 'Teléfono no válido')
    .required('Campo obligatorio'),
  tipoEvento: Yup.string().required('Selecciona un tipo de evento'),
  tipoServicio: Yup.string().required('Selecciona un tipo de servicio'),
  fecha: Yup.string().required('Selecciona una fecha aproximada'),
  localizacion: Yup.string().trim().required('Campo obligatorio'),
  comentarios: Yup.string(),
  privacidad: Yup.boolean().oneOf([true], 'Debes aceptar la política de privacidad para continuar'),
});

const EVENT_TYPES = ['Corporativo', 'Boda', 'Celebración privada', 'Festival', 'Inauguración', 'Lanzamiento', 'Otro'];
const SERVICE_TYPES = ['Catering Estándar', 'Catering Premium', 'Premium + Sushiman'];

const MENU_PROPS = {
  PaperProps: {
    sx: {
      bgcolor: 'var(--color-bg-card)',
      border: '1px solid var(--color-border)',
      '& .MuiMenuItem-root': {
        color: 'var(--color-text-primary)',
        fontSize: '0.92rem',
        '&:hover': { bgcolor: 'var(--color-accent-muted)' },
        '&.Mui-selected': {
          bgcolor: 'rgba(200,169,110,0.12)',
          color: 'var(--color-accent)',
          '&:hover': { bgcolor: 'rgba(200,169,110,0.18)' },
        },
      },
    },
  },
};

const fieldSx = (hasError: boolean | undefined) => ({
  '& .MuiOutlinedInput-root': {
    bgcolor: 'var(--color-bg-card)',
    '& fieldset': { borderColor: hasError ? '#ef4444' : 'var(--color-border)' },
    '&:hover fieldset': { borderColor: hasError ? '#ef4444' : 'var(--color-accent)' },
    '&.Mui-focused fieldset': { borderColor: hasError ? '#ef4444' : 'var(--color-accent)' },
  },
  '& .MuiInputLabel-root': { color: hasError ? '#ef4444' : 'var(--color-text-secondary)' },
  '& .MuiInputLabel-root.Mui-focused': { color: hasError ? '#ef4444' : 'var(--color-accent)' },
  '& .MuiOutlinedInput-input': { color: 'var(--color-text-primary)' },
  '& .MuiSelect-icon': { color: 'var(--color-text-secondary)' },
  '& .MuiFormHelperText-root': { color: '#ef4444', fontSize: '0.75rem' },
});

const SuccessPanel: React.FC<{ onReset: () => void }> = ({ onReset }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.45 }}
  >
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: 'rgba(200,169,110,0.12)',
          border: '2px solid var(--color-accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 36, color: 'var(--color-accent)' }} />
      </Box>
      <Typography variant="h5" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--color-text-primary)', mb: 1.5 }}>
        ¡Solicitud enviada!
      </Typography>
      <Typography sx={{ color: 'var(--color-text-secondary)', lineHeight: 1.8, maxWidth: 400, mx: 'auto', mb: 4 }}>
        Hemos recibido tu solicitud. Nos pondremos en contacto contigo en menos de 24 horas para hablar de tu evento.
      </Typography>
      <Button
        variant="outlined"
        onClick={onReset}
        sx={{
          color: 'var(--color-accent)',
          borderColor: 'rgba(200,169,110,0.5)',
          '&:hover': { bgcolor: 'var(--color-accent-muted)', borderColor: 'var(--color-accent)' },
        }}
      >
        Enviar otra solicitud
      </Button>
    </Box>
  </motion.div>
);

interface Props {
  open: boolean;
  onClose: () => void;
}

const Contact: React.FC<Props> = ({ open, onClose }) => {
  const [sendStatus, setSendStatus] = useState<SendStatus>('idle');
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const handleClose = () => {
    if (sendStatus !== 'sending') onClose();
  };

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    setSendStatus('sending');
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
        {
          from_name: `${values.nombre} ${values.apellidos}`,
          from_email: values.email,
          phone: values.telefono,
          event_type: values.tipoEvento,
          service_type: values.tipoServicio,
          event_date: values.fecha,
          location: values.localizacion,
          message: values.comentarios || '—',
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!,
      );

      emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID!,
        {
          to_name: values.nombre,
          to_email: values.email,
          event_type: values.tipoEvento,
          service_type: values.tipoServicio,
          event_date: values.fecha,
          location: values.localizacion,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!,
      ).catch((err) => console.error('<Error>', err));

      setSendStatus('success');
      resetForm();
    } catch {
      setSendStatus('error');
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        slotProps={{
          paper: {
            sx: {
              bgcolor: '#0d0d0d',
              backgroundImage: 'none',
              border: '1px solid var(--color-border-hover)',
              borderRadius: 2,
              m: { xs: 1.5, sm: 3 },
              width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 48px)' },
            },
          },
        }}
      >
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            px: { xs: 3, md: 5 },
            pt: { xs: 3, md: 4 },
            pb: 1,
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'var(--color-accent)', letterSpacing: '0.3em', fontSize: '0.7rem' }}
            >
              Contacto
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontFamily: 'var(--font-heading)', color: 'var(--color-text-primary)', mt: 0.25 }}
            >
              ¿Listo para darle otro rollo a tu evento?
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={sendStatus === 'sending'}
            aria-label="Cerrar formulario de contacto"
            sx={{ color: 'var(--color-text-secondary)', mt: -0.5, ml: 2, flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: { xs: 3, md: 5 }, pb: { xs: 3, md: 4 } }}>
          <Typography
            sx={{ color: 'var(--color-text-secondary)', lineHeight: 1.8, mb: 4, fontSize: '0.9rem' }}
          >
            Cuéntanos tu idea y diseñaremos juntos el catering de sushi que convierta tu celebración en una auténtica experiencia Sushigi.
          </Typography>

          <AnimatePresence mode="wait">
            {sendStatus === 'success' ? (
              <SuccessPanel key="success" onReset={() => setSendStatus('idle')} />
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Formik
                  initialValues={INITIAL_VALUES}
                  validationSchema={SCHEMA}
                  onSubmit={handleSubmit}
                >
                  {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched }) => (
                    <Form noValidate>
                      <Grid container spacing={2.5}>

                        {/* Nombre */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            name="nombre"
                            label="Nombre *"
                            value={values.nombre}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.nombre && Boolean(errors.nombre)}
                            helperText={touched.nombre && errors.nombre}
                            sx={fieldSx(touched.nombre && Boolean(errors.nombre))}
                          />
                        </Grid>

                        {/* Apellidos */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            name="apellidos"
                            label="Apellidos *"
                            value={values.apellidos}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.apellidos && Boolean(errors.apellidos)}
                            helperText={touched.apellidos && errors.apellidos}
                            sx={fieldSx(touched.apellidos && Boolean(errors.apellidos))}
                          />
                        </Grid>

                        {/* Email */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            name="email"
                            label="Email *"
                            type="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            sx={fieldSx(touched.email && Boolean(errors.email))}
                          />
                        </Grid>

                        {/* Teléfono */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            name="telefono"
                            label="Teléfono *"
                            value={values.telefono}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.telefono && Boolean(errors.telefono)}
                            helperText={touched.telefono && errors.telefono}
                            sx={fieldSx(touched.telefono && Boolean(errors.telefono))}
                          />
                        </Grid>

                        {/* Tipo de evento */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            select
                            name="tipoEvento"
                            label="Tipo de evento *"
                            value={values.tipoEvento}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.tipoEvento && Boolean(errors.tipoEvento)}
                            helperText={touched.tipoEvento && errors.tipoEvento}
                            sx={fieldSx(touched.tipoEvento && Boolean(errors.tipoEvento))}
                            slotProps={{ select: { MenuProps: MENU_PROPS } as any }}
                          >
                            {EVENT_TYPES.map((t) => (
                              <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        {/* Tipo de servicio */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            select
                            name="tipoServicio"
                            label="Tipo de servicio *"
                            value={values.tipoServicio}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.tipoServicio && Boolean(errors.tipoServicio)}
                            helperText={touched.tipoServicio && errors.tipoServicio}
                            sx={fieldSx(touched.tipoServicio && Boolean(errors.tipoServicio))}
                            slotProps={{ select: { MenuProps: MENU_PROPS } as any }}
                          >
                            {SERVICE_TYPES.map((t) => (
                              <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        {/* Fecha */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            name="fecha"
                            label="Fecha aproximada *"
                            type="date"
                            value={values.fecha}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.fecha && Boolean(errors.fecha)}
                            helperText={touched.fecha && errors.fecha}
                            sx={fieldSx(touched.fecha && Boolean(errors.fecha))}
                            slotProps={{
                              inputLabel: { shrink: true },
                              htmlInput: { min: new Date().toISOString().split('T')[0] },
                            }}
                          />
                        </Grid>

                        {/* Localización */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            name="localizacion"
                            label="Localización del evento *"
                            value={values.localizacion}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.localizacion && Boolean(errors.localizacion)}
                            helperText={touched.localizacion && errors.localizacion}
                            sx={fieldSx(touched.localizacion && Boolean(errors.localizacion))}
                          />
                        </Grid>

                        {/* Comentarios */}
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            fullWidth
                            name="comentarios"
                            label="Comentarios adicionales"
                            multiline
                            rows={3}
                            value={values.comentarios}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            sx={fieldSx(false)}
                            placeholder="Número aproximado de invitados, preferencias del menú, necesidades especiales…"
                            slotProps={{
                              input: {
                                sx: { '&::placeholder': { color: 'var(--color-text-secondary)', opacity: 0.6 } },
                              },
                            }}
                          />
                        </Grid>

                        {/* Checkbox privacidad */}
                        <Grid size={{ xs: 12 }}>
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: touched.privacidad && errors.privacidad
                                ? 'rgba(239,68,68,0.6)'
                                : 'var(--color-border)',
                              borderRadius: 1.5,
                              p: 2,
                              bgcolor: 'var(--color-bg-card)',
                              transition: 'border-color 0.2s',
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={values.privacidad}
                                  onChange={(e) => {
                                    setFieldValue('privacidad', e.target.checked);
                                    setFieldTouched('privacidad', true, false);
                                  }}
                                  sx={{
                                    color: touched.privacidad && errors.privacidad
                                      ? '#ef4444'
                                      : 'var(--color-border-hover)',
                                    '&.Mui-checked': { color: 'var(--color-accent)' },
                                    alignSelf: 'flex-start',
                                    mt: '-2px',
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'var(--color-text-secondary)',
                                    fontSize: '0.82rem',
                                    lineHeight: 1.7,
                                  }}
                                >
                                  He leído y acepto la{' '}
                                  <Box
                                    component="span"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setPrivacyOpen(true);
                                    }}
                                    sx={{
                                      color: 'var(--color-accent)',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      textUnderlineOffset: '3px',
                                    }}
                                  >
                                    Política de privacidad
                                  </Box>
                                  {' '}de Terrasuyi, S.L. Consiento el tratamiento de mis datos personales y su
                                  envío a través de{' '}
                                  <strong style={{ color: 'var(--color-text-primary)' }}>EmailJS</strong>{' '}
                                  para gestionar mi solicitud de catering. *
                                </Typography>
                              }
                              sx={{ alignItems: 'flex-start', m: 0 }}
                            />
                            {touched.privacidad && errors.privacidad && (
                              <FormHelperText sx={{ color: '#ef4444', fontSize: '0.75rem', ml: 4, mt: 0.5 }}>
                                {errors.privacidad}
                              </FormHelperText>
                            )}
                          </Box>
                        </Grid>

                        {/* Error alert */}
                        {sendStatus === 'error' && (
                          <Grid size={{ xs: 12 }}>
                            <Alert
                              severity="error"
                              icon={<ErrorIcon />}
                              sx={{
                                bgcolor: 'rgba(239,68,68,0.08)',
                                color: '#fca5a5',
                                border: '1px solid rgba(239,68,68,0.3)',
                                '& .MuiAlert-icon': { color: '#ef4444' },
                              }}
                            >
                              No se pudo enviar el mensaje. Comprueba tu conexión e inténtalo de nuevo.
                            </Alert>
                          </Grid>
                        )}

                        {/* Submit */}
                        <Grid size={{ xs: 12 }}>
                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={sendStatus === 'sending'}
                            sx={{
                              bgcolor: 'var(--color-accent)',
                              color: '#0d0d0d',
                              fontWeight: 700,
                              py: 1.9,
                              fontSize: '1rem',
                              letterSpacing: '0.05em',
                              '&:hover': { bgcolor: 'var(--color-accent-hover)' },
                              '&.Mui-disabled': { bgcolor: 'rgba(200,169,110,0.35)', color: 'rgba(0,0,0,0.5)' },
                            }}
                          >
                            {sendStatus === 'sending' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CircularProgress size={20} sx={{ color: '#0d0d0d' }} />
                                Enviando…
                              </Box>
                            ) : (
                              'Enviar solicitud'
                            )}
                          </Button>
                        </Grid>

                      </Grid>
                    </Form>
                  )}
                </Formik>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>

      <PrivacyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </>
  );
};

export default Contact;
